import { useCallback, useEffect, useMemo, useState } from "react"
import { useParams } from "react-router-dom"
import api from "../api"
import { ButtonStatus, CompanyStatus, CompanyType, CourseType, FileType } from "../common/constants"
import Breadcrumb from "../components/Breadcrumb"
import Button from "../components/Button"
import AlertDialog from "../components/dialogs/AlertDialog"
import HeaderFooterLayout from "../components/layouts/HeaderFooterLayout"
import MeatBallsMenu from "../components/MeatBallsMenu"
import Select from "../components/Select"
import Skeleton from "../components/Skeleton"
import Table from "../components/Table"
import TextInput from "../components/TextInput"
import Uploader from "../components/Uploader"
import { DialogStatus } from "../enums"
import styles from "./Company.module.css"

const Company = () => {
  const { companyId } = useParams()

  const typeOptions = useMemo(() => [
    { id: 0, name: "Didattica B2B", value: CompanyType.B2BTeaching },
    { id: 1, name: "Lead Generation Provider", value: CompanyType.LeadGenerationProvider },
        { id: 2, name: "Training Buyer", value: CompanyType.TrainingBuyer },
        { id: 3, name: "Event Partner", value: CompanyType.EventPartner },
        { id: 4, name: "Other", value: CompanyType.Other },
  ], [])

  const statusOptions = useMemo(() => [
    { id: 0, name: "Attiva", value: CompanyStatus.Active },
    { id: 1, name: "Inattiva", value: CompanyStatus.Inactive },
  ], [])

  const [company, setCompany] = useState(null)
  const [loading, setLoading] = useState(false)
  const [savingStatus, setSavingStatus] = useState(null)
  const [courseSavingStatus, setCourseSavingStatus] = useState(null)
  const [courseOptions, setCourseOptions] = useState([])
  const [filteredCourseOptions, setFilteredCourseOptions] = useState([])
  const [selectedCourse, setSelectedCourse] = useState(null)
  const [maxUsers, setMaxUsers] = useState("")

  const [name, setName] = useState("")
  const [selectedType, setSelectedType] = useState(typeOptions[0])
  const [selectedStatus, setSelectedStatus] = useState(statusOptions[0])
  const [email, setEmail] = useState("")
  const [phone, setPhone] = useState("")
  const [website, setWebsite] = useState("")
  const [vatNumber, setVatNumber] = useState("")
  const [logoUrl, setLogoUrl] = useState(null)
  const [uploaderReset, setUploaderReset] = useState(false)

  const [alert, setAlert] = useState({
    open: false,
    title: "",
    text: "",
    status: DialogStatus.Default,
    actions: [],
  })

  const hydrateCompany = useCallback((data) => {
    setCompany(data)
    setName(data?.name || "")
    setSelectedType(typeOptions.find((option) => option.value === data?.type) || typeOptions[0])
    setSelectedStatus(statusOptions.find((option) => option.value === data?.status) || statusOptions[0])
    setEmail(data?.email || "")
    setPhone(data?.phone || "")
    setWebsite(data?.website || "")
    setVatNumber(data?.vat_number || "")
    setLogoUrl(data?.logo || null)
    setUploaderReset((prev) => !prev)
  }, [statusOptions, typeOptions])

  const loadCompany = useCallback(async () => {
    setLoading(true)
    try {
      const response = await api.get(`/admin/companies/${companyId}`)
      hydrateCompany(response)
    } catch (e) {
      console.error(e)
    }
    setLoading(false)
  }, [companyId, hydrateCompany])

  const loadCourses = useCallback(async () => {
    try {
      const response = await api.get(`/admin/courses?complete=false`)
      const list = Array.isArray(response) ? response : (response?.courses || [])
      const mapped = list
        .filter((course) => course?.type !== CourseType.Webinar)
        .map((course) => ({
          id: course.id,
          value: course.id,
          name: course.name,
        }))
        .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), 'it'))

      setCourseOptions(mapped)
      setFilteredCourseOptions(mapped)
    } catch (e) {
      console.error(e)
    }
  }, [])

  useEffect(() => {
    loadCompany()
    loadCourses()
  }, [loadCompany, loadCourses])

  useEffect(() => {
    setFilteredCourseOptions(courseOptions)
  }, [courseOptions])

  const closeAlert = useCallback(() => {
    setAlert({
      open: false,
      title: "",
      text: "",
      status: DialogStatus.Default,
      actions: [],
    })
  }, [])

  const searchCourses = useCallback((value) => {
    const query = String(value || "").trim().toLowerCase()
    if (!query) {
      setFilteredCourseOptions(courseOptions)
      return
    }

    setFilteredCourseOptions(
      courseOptions.filter((course) => String(course?.name || "").toLowerCase().includes(query))
    )
  }, [courseOptions])

  const getCompanyTypeLabel = useCallback((type) => {
    return typeOptions.find((option) => option.value === type)?.name || type || "N/A"
  }, [typeOptions])

  const saveCompany = async () => {
    if (!name.trim()) return

    setSavingStatus(ButtonStatus.Loading)
    try {
      await api.put(`/admin/companies/${companyId}`, {
        name: name.trim(),
        type: selectedType?.value,
        status: selectedStatus?.value,
        logo: logoUrl || null,
        email: email.trim() || null,
        phone: phone.trim() || null,
        website: website.trim() || null,
        vat_number: vatNumber.trim() || null,
      })
      setSavingStatus(ButtonStatus.Success)
      await loadCompany()
    } catch (e) {
      console.error(e)
      setSavingStatus(ButtonStatus.Error)
    }
  }

  const currentCourseAssociation = useMemo(() => {
    return (company?.courses || []).find((item) => {
      const itemCourseId = item?.course_id ?? item?.course?.id
      return String(itemCourseId) === String(selectedCourse?.value)
    }) || null
  }, [company, selectedCourse])

  const canSaveCourse = useMemo(() => {
    const parsed = Number(maxUsers)
    return Boolean(selectedCourse?.value) && Number.isInteger(parsed) && parsed > 0
  }, [selectedCourse, maxUsers])

  const startEditCourse = useCallback((companyCourse) => {
    const courseId = companyCourse?.course_id ?? companyCourse?.course?.id
    const courseName = companyCourse?.course?.name || `Corso ${courseId}`
    setSelectedCourse(courseOptions.find((option) => String(option.value) === String(courseId)) || {
      id: courseId,
      value: courseId,
      name: courseName,
    })
    setMaxUsers(String(companyCourse?.max_users || ""))
  }, [courseOptions])

  const upsertCourse = async () => {
    if (!canSaveCourse) return

    setCourseSavingStatus(ButtonStatus.Loading)
    try {
      await api.put(`/admin/companies/${companyId}/courses/${selectedCourse.value}`, {
        max_users: Number(maxUsers),
      })
      setCourseSavingStatus(ButtonStatus.Success)
      setSelectedCourse(null)
      setMaxUsers("")
      await loadCompany()
    } catch (e) {
      console.error(e)
      setCourseSavingStatus(ButtonStatus.Error)
    }
  }

  const removeCourse = useCallback(async (companyCourse) => {
    setAlert((prev) => ({ ...prev, status: DialogStatus.Loading, actions: [] }))
    try {
      const courseId = companyCourse?.course_id ?? companyCourse?.course?.id
      await api.delete(`/admin/companies/${companyId}/courses/${courseId}`)
      if (String(selectedCourse?.value) === String(courseId)) {
        setSelectedCourse(null)
        setMaxUsers("")
      }
      await loadCompany()
      setAlert({
        open: true,
        title: "Corso disassegnato",
        text: "Il corso è stato rimosso dall'azienda.",
        status: DialogStatus.Success,
        actions: [{ label: "OK", onClick: closeAlert }],
      })
    } catch (e) {
      console.error(e)
      setAlert({
        open: true,
        title: "Impossibile disassegnare",
        text: e?.detail || "Controlla che non ci siano utenti aziendali già registrati al corso.",
        status: DialogStatus.Error,
        actions: [{ label: "CHIUDI", onClick: closeAlert }],
      })
    }
  }, [closeAlert, companyId, loadCompany, selectedCourse])

  const askRemoveCourse = useCallback((companyCourse) => {
    setAlert({
      open: true,
      title: "Disassegna corso",
      text: `Vuoi davvero disassociare ${companyCourse?.course?.name || "questo corso"} da ${company?.name || "questa azienda"}?`,
      status: DialogStatus.Default,
      actions: [
        { label: "ANNULLA", onClick: closeAlert, inverse: true },
        { label: "SI, DISASSOCIA", onClick: () => removeCourse(companyCourse) },
      ],
    })
  }, [closeAlert, company?.name, removeCourse])

  const courseRows = useMemo(() => {
    return (company?.courses || []).map((companyCourse) => ({
      ...companyCourse,
      id: companyCourse.id,
      course_name: companyCourse?.course?.name || `Corso ${companyCourse?.course_id}`,
      max_users: companyCourse?.max_users ?? 0,
      registered_users: companyCourse?.registered_users ?? 0,
      available_slots: companyCourse?.available_slots ?? 0,
      actions: {
        value: companyCourse.id,
        type: "string",
        component: (
          <MeatBallsMenu
            style={{ padding: "0.5rem" }}
            actions={[
              { label: "Modifica limite", onClick: () => startEditCourse(companyCourse) },
              { label: "Disassegna", style: { color: "var(--sf-red)" }, onClick: () => askRemoveCourse(companyCourse) },
            ]}
          />
        ),
      },
    }))
  }, [askRemoveCourse, company?.courses, startEditCourse])

  return (
    <HeaderFooterLayout>
      <div className={styles.container}>
        <div className={styles.section}>
          <div className={styles.sectionInner}>
            <Breadcrumb items={[{label: 'Utenti', to: '/users'}, {label: 'Aziende', to: '/users/companies'}, {label: company?.name || 'Profilo'}]} />
            <header className={styles.pageHeader}><h1>Profilo azienda</h1><p>Gestisci i dati aziendali, i corsi e gli amministratori associati.</p></header>

            {loading && !company ? (
              <div className={styles.content}>
                <div className={styles.leftColumn}>
                  <Skeleton width={"100%"} height={240} />
                </div>
                <div className={styles.rightColumn}>
                  <Skeleton width={"100%"} height={220} />
                  <Skeleton width={"100%"} height={280} />
                </div>
              </div>
            ) : (
              <div className={styles.content}>
                <div className={styles.leftColumn}>
                  <div className={styles.profile}>
                    <Uploader
                      crop={true}
                      shape="square"
                      aspect={1}
                      type={FileType.Image}
                      multiple={false}
                      reset={uploaderReset}
                      preview={logoUrl}
                      message="Trascina o seleziona il logo aziendale"
                      label="Scegli logo"
                      iconButtons={true}
                      onComplete={(uploads) => setLogoUrl(uploads?.[0]?.url || null)}
                      onDelete={() => setLogoUrl(null)}
                    />

                    <div className={styles.bio}>
                      <div className={styles.username}>{company?.name || "Azienda"}</div>
                      {company?.client_id && <div className={styles.email}>Client ID: {company.client_id}</div>}
                    </div>
                  </div>

                    <div className={styles.subtitle}>Riepilogo</div>
                    <div className={styles.summaryItem}><strong>Tipologia:</strong> <span>{getCompanyTypeLabel(selectedType?.value ?? company?.type)}</span></div>
                    <div className={styles.summaryItem}><strong>Admin associati:</strong> <span>{company?.admins?.length || 0}</span></div>
                    <div className={styles.summaryItem}><strong>Corsi associati:</strong> <span>{company?.courses?.length || 0}</span></div>
                </div>

                <div className={styles.rightColumn}>
                  <div className={styles.card}>
                    <div className={styles.cardTitle}>Dati azienda</div>
                    <div className={styles.inputs}>
                      <TextInput appearance="modern" value={name} type="text" label="Nome azienda" placeholder="Nome azienda" onKeyUp={setName} />

                      <div className={styles.row}>
                        <div className={styles.field}><span className={styles.fieldLabel}>Tipologia</span><Select appearance="modern" portal selected={selectedType} options={typeOptions} onSelect={setSelectedType} /></div>
                        <div className={styles.field}><span className={styles.fieldLabel}>Stato</span><Select appearance="modern" portal selected={selectedStatus} options={statusOptions} onSelect={setSelectedStatus} /></div>
                      </div>

                      <div className={styles.row}>
                        <TextInput appearance="modern" value={email} type="text" label="Email" placeholder="Email" onKeyUp={setEmail} />
                        <TextInput appearance="modern" value={phone} type="text" label="Telefono" placeholder="Telefono" onKeyUp={setPhone} />
                      </div>

                      <div className={styles.row}>
                        <TextInput appearance="modern" value={website} type="text" label="Sito web" placeholder="Sito web" onKeyUp={setWebsite} />
                        <TextInput appearance="modern" value={vatNumber} type="text" label="Partita IVA" placeholder="Partita IVA" onKeyUp={setVatNumber} />
                      </div>

                      <div className={styles.buttonRow}>
                        <Button
                          style={{ borderRadius: 8, padding: ".65rem 1rem", fontSize: ".75rem" }}
                          accentColor={"var(--primary)"}
                          disabled={!name.trim() || savingStatus === ButtonStatus.Loading}
                          status={savingStatus}
                          onClick={saveCompany}
                        >
                          Salva modifiche
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className={styles.card}>
                    <div className={styles.cardTitle}>Corsi associati</div>

                    <div className={styles.courseAssignRow}>
                      <div className={styles.courseField}>
                        <div className={styles.field}><span className={styles.fieldLabel}>Corso</span><Select appearance="modern" portal
                          selected={selectedCourse}
                          options={filteredCourseOptions}
                          placeholder="Seleziona un corso"
                          onSearch={searchCourses}
                          searchPlaceholder="Cerca corso"
                          onSelect={(course) => setSelectedCourse(course ?? null)}
                        /></div>
                      </div>

                      <div className={styles.courseLimitField}>
                        <TextInput appearance="modern"
                          value={maxUsers}
                          type="number"
                          label="Numero massimo utenti" placeholder="Numero massimo utenti"
                          onKeyUp={setMaxUsers}
                        />
                      </div>

                      <div className={styles.courseAction}>
                        <Button
                          style={{ borderRadius: 8, padding: ".65rem 1rem", fontSize: ".75rem" }}
                          accentColor={"var(--primary)"}
                          disabled={!canSaveCourse || courseSavingStatus === ButtonStatus.Loading}
                          status={courseSavingStatus}
                          onClick={upsertCourse}
                        >
                          {currentCourseAssociation ? "Salva limite" : "Assegna corso"}
                        </Button>
                      </div>
                    </div>

                    {currentCourseAssociation && (
                      <div className={styles.infoText}>
                        Il corso è già associato. Salvando aggiornerai il limite attuale.
                      </div>
                    )}

                    <div className={styles.integratedTable}><Table integrated
                      columns={[
                        { key: "course_name", label: "CORSO", type: "string" },
                        { key: "max_users", label: "MAX UTENTI", type: "number" },
                        { key: "registered_users", label: "REGISTRATI", type: "number" },
                        { key: "available_slots", label: "POSTI DISPONIBILI", type: "number" },
                        { key: "actions", label: "AZIONI", type: "custom", handleClick: true },
                      ]}
                      loading={loading}
                      elements={courseRows}
                      onRowClick={startEditCourse}
                      defaultSort={{ key: "course_name", direction: "asc" }}
                    /></div>
                  </div>

                  <div className={styles.card}>
                    <div className={styles.cardTitle}>Admin associati</div>
                    <div className={styles.inputs}>
                      {(company?.admins || []).length === 0 && (
                        <div className={styles.emptyState}>Nessun admin associato a questa azienda.</div>
                      )}

                      {(company?.admins || []).map((admin) => (
                        <div key={admin.id} className={styles.adminItem}>
                          <div>
                            <div className={styles.adminName}>{admin.name} {admin.surname}</div>
                            <div className={styles.adminMeta}>{admin.email}</div>
                          </div>
                          <div className={styles.adminRole}>{admin.company_role || admin.role}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <AlertDialog
          open={alert.open}
          title={alert.title}
          text={alert.text}
          status={alert.status}
          onClose={closeAlert}
          actions={alert.actions}
        />
      </div>
    </HeaderFooterLayout>
  )
}

export default Company
